import React, { useState } from "react";

/**
 * Barra de filtros padrão do site: abre e fecha, resume o que está aplicado e alinha os campos numa grade.
 *
 * Por que colapsar: a barra empurrava a lista para baixo e obrigava a rolar para ver o primeiro resultado —
 * quem chega quer ver o RESULTADO, não o painel de controle. Fechada, ela sai do caminho.
 *
 * Por que o resumo é obrigatório: filtro escondido é filtro esquecido. Fechada, a barra mostra em texto tudo
 * que está aplicado (inclusive o que já vinha preenchido, como "match ≥ 20%"), senão a pessoa vê a lista curta
 * e conclui que o site não tem candidato — em vez de perceber que ela mesma cortou.
 *
 * Por que é UM componente e não três parecidos: mural, candidatos da vaga e banco de talentos tinham cada um
 * o seu desenho (grade aqui, campos soltos ali, "Limpar" em cima num e embaixo no outro). Filtrar é a MESMA
 * tarefa nos três — quando o controle muda de forma a cada aba, quem usa reaprende a tela toda vez.
 */
export default function FiltrosColapsaveis({ resumo = [], onLimpar, children, abertoInicial = false, rotulo = "Filtros", ajuda }) {
  const [aberto, setAberto] = useState(abertoInicial);
  const ativos = resumo.filter(Boolean);

  return (
    <div className="rounded-2xl border border-gray-200 bg-white shadow-sm">
      <div className="flex flex-wrap items-center gap-2 p-3">
        <button type="button" onClick={() => setAberto((o) => !o)} aria-expanded={aberto}
          className="flex items-center gap-1.5 text-sm font-semibold text-gray-700 hover:text-primary">
          <span aria-hidden="true">🔎</span> {rotulo}
          <span className={`text-xs transition-transform ${aberto ? "rotate-180" : ""}`} aria-hidden="true">▾</span>
        </button>

        {!aberto && ativos.length > 0 && (
          <div className="flex flex-wrap items-center gap-1.5">
            {ativos.map((t, i) => (
              <span key={i} className="rounded-full bg-primary/10 text-primary text-xs font-semibold px-2 py-0.5">{t}</span>
            ))}
          </div>
        )}
        {!aberto && ativos.length === 0 && <span className="text-xs text-gray-400">nenhum filtro aplicado</span>}

        {onLimpar && ativos.length > 0 && (
          <button type="button" onClick={onLimpar} className="text-sm text-primary hover:underline ml-auto">Limpar</button>
        )}
      </div>

      {aberto && (
        <div className="border-t border-gray-100 p-3">
          {/* Grade em vez de campos em fila: com larguras diferentes a barra fica serrilhada e o olho não
              encontra o campo. Uma coluna no celular, duas no tablet, três no desktop. */}
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">{children}</div>
          {ajuda && <p className="mt-2 text-[11px] text-gray-400">{ajuda}</p>}
        </div>
      )}
    </div>
  );
}

/** Classe única dos controles de filtro — o que faz as três telas parecerem a mesma tela. */
export const campoFiltro =
  "w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary";

/** Campo com rótulo à esquerda (número, faixa) ocupando UMA célula da grade, como os selects. */
export function CampoRotulado({ rotulo, sufixo, children }) {
  return (
    <label className="flex items-center gap-2 text-sm text-gray-600">
      <span className="whitespace-nowrap">{rotulo}</span>
      {children}
      {sufixo && <span className="text-gray-400">{sufixo}</span>}
    </label>
  );
}
